import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Typography,
  Card,
  Grid,
  Container,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  selectAllJobs,
  selectJobsStatus,
  selectJobsError,
  selectUniqueJobRoles,
} from '../../store/jobsSlice';
import WorkIcon from '@mui/icons-material/Work';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ReplayIcon from '@mui/icons-material/Replay';

const JobList = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Redux state
  const jobs = useSelector(selectAllJobs);
  const status = useSelector(selectJobsStatus);
  const error = useSelector(selectJobsError);
  const uniqueJobRoles = useSelector(selectUniqueJobRoles);

  // Local state
  const [locationFilter, setLocationFilter] = useState('');
  const [function_, setFunction] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1); // Default zoom level (100%)
  const [progress, setProgress] = useState(0); // Progress state for loading animation
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isLaptop = useMediaQuery(theme.breakpoints.up('lg'));
  const Large = useMediaQuery('(min-width:1536px) and (max-width:1600px)');
  const openRolesSectionRef = useRef(null);

  // Detect zoom level
  useEffect(() => {
    const detectZoom = () => {
      const ratio = window.devicePixelRatio || 1;
      const zoom = (ratio * 100) / (window.visualViewport?.scale || 1);
      setZoomLevel(zoom);
    };

    detectZoom();
    window.addEventListener('resize', detectZoom);
    return () => window.removeEventListener('resize', detectZoom);
  }, []);

  // Animate progress when loading
  useEffect(() => {
    let interval;
    if (status === 'loading') {
      interval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) return 100;
          return prevProgress + 25;
        });
      }, 500);
    } else {
      setProgress(0); // Reset progress when not loading
    }
    return () => clearInterval(interval);
  }, [status]);

  // Handle scrolling
  useEffect(() => {
    const shouldScroll = localStorage.getItem('shouldScrollToJobs') === 'true';
    if (shouldScroll) {
      localStorage.removeItem('shouldScrollToJobs');
      const scrollToOpenRoles = () => {
        const section = document.getElementById('open-roles-section');
        if (section) {
          const yOffset = section.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: yOffset,
            behavior: 'smooth',
          });
          return true;
        }
        if (openRolesSectionRef.current) {
          openRolesSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
          return true;
        }
        window.scrollTo({
          top: 500,
          behavior: 'smooth',
        });
        return true;
      };
      setTimeout(scrollToOpenRoles, 0);
      setTimeout(scrollToOpenRoles, 100);
    }
  }, []);

  // Filter jobs based on selected filters
  const filteredJobs = jobs.filter((job) => {
    const locationMatch = !locationFilter || job.Location.includes(locationFilter);
    const functionMatch = !function_ || job.JobRole.includes(function_);
    return locationMatch && functionMatch;
  });

  // Extract unique locations for the dropdown
  const uniqueLocations = [...new Set(jobs.map((job) => job.Location))].filter(Boolean);

  const handleResetFilters = () => {
    setLocationFilter('');
    setFunction('');
  };

  // Conditionally apply paddingX based on zoom level
  const getPaddingX = () => {
    if (Math.abs(zoomLevel - 110) < 1) {
      return { xs: '16px', sm: '32px', md: '60px', lg: '219px', xl: Large ? '169px' : '149px' };
    }
    return { xs: '16px', sm: '32px', md: '60px', lg: '219px', xl: '69px' };
  };

  return (
    <Box
      id="open-roles-section"
      ref={openRolesSectionRef}
      sx={{ backgroundColor: '#F2F3F5', py: 4, pb: { xs: 4, sm: 6, md: 8 } }}
    >
      <Container
        disableGutters
        maxWidth="xl"
        sx={{
          paddingX: getPaddingX(),
        }}
      >
        <Box sx={{ textAlign: 'center', my: 8 }}>
          <Typography
            sx={{
              fontSize: isMobile ? '16px' : isTablet ? '18px' : '18px',
              textTransform: 'uppercase',
              color: '#0087B4',
              fontWeight: 600,
            }}
          >
            Opportunities at DevAngles
          </Typography>
          <Typography sx={{ fontWeight: 400, fontSize: '42px', mb: 4 }}>
            Join The DevAngles Team
          </Typography>
        </Box>

        {/* Filter Section */}
        <Box sx={{ display: 'flex', gap: 2, mb: 4 }}>
          <FormControl fullWidth>
            <InputLabel id="job-title-label" color='black'>Select Job Titles</InputLabel>
            <Select
              labelId="job-title-label"
              id="job-title-select"
              value={function_}
              label="Select Job Titles"
              onChange={(e) => setFunction(e.target.value)}
              sx={{
                bgcolor: 'white',
                height: '56px',
                borderRadius: '15px',
                fontWeight: 300,
                fontSize: { xs: '12px', sm: '14px', md: '18px' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{ bgcolor: 'white', fontWeight: 300, fontSize: { xs: '12px', sm: '14px', md: '18px' } }}
              >
                All Roles
              </MenuItem>
              {uniqueJobRoles.map((role) => (
                <MenuItem
                  key={role}
                  value={role}
                  sx={{ bgcolor: 'white', fontWeight: 300, fontSize: { xs: '12px', sm: '14px', md: '18px' } }}
                >
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="location-label" color='balck'>Select Location</InputLabel>
            <Select
              labelId="location-label"
              id="location-select"
              value={locationFilter}
              label="Select Location"
              onChange={(e) => setLocationFilter(e.target.value)}
              sx={{
                bgcolor: 'white',
                height: '56px',
                borderRadius: '15px',
                fontWeight: 300,
                fontSize: { xs: '12px', sm: '14px', md: '18px' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'black',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{ bgcolor: 'white', fontWeight: 300, fontSize: { xs: '12px', sm: '14px', md: '18px' } }}
              >
                All Locations
              </MenuItem>
              {uniqueLocations.map((location) => (
                <MenuItem
                  key={location}
                  value={location}
                  sx={{ bgcolor: 'white', fontWeight: 300, fontSize: { xs: '12px', sm: '14px', md: '18px' } }}
                >
                  {location}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            sx={{
              bgcolor: '#0087B4',
              color: 'white',
              width: { xs: '220px', sm: '220px', md: '280px' },
              height: '54px',
              textTransform: 'none',
              fontSize: { xs: '14px', sm: '1rem', md: '18px' },
              borderRadius: '15px',
              fontWeight: 300,
            }}
            onClick={handleResetFilters}
          >
            Reset Filters
          </Button>
        </Box>

        {/* Job Cards Grid */}
        {status === 'loading' ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress
              variant="determinate"
              value={progress}
              size={60}
              thickness={4}
              sx={{ color: '#0087B4' }}
            />
          </Box>
        ) : status === 'failed' ? (
          <Box sx={{ textAlign: 'center', my: 4 }}>
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              Oops, it looks like our job listings are taking a quick break! Please try again in a moment, or check back
              soon for exciting opportunities with DevAngles.
            </Typography>
            {/* <Button
              variant="contained"
              startIcon={<ReplayIcon />}
              sx={{
                mt: 2,
                bgcolor: '#0087B4',
                color: 'white',
                textTransform: 'none',
                fontSize: '16px',
                borderRadius: '15px',
              }}
              onClick={() => dispatch(fetchJobs())} // Optional: Reintroduce dispatch for retry
            >
              Retry
            </Button> */}
          </Box>
        ) : filteredJobs.length === 0 ? (
          <Typography sx={{ textAlign: 'center', my: 4 ,fontWeight: 300,fontSize: { xs: '22px', sm: '25px', md: '25px' },}}>
            No matching jobs found.
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {filteredJobs.map((job) => (
              <Grid item xs={12} md={4} key={job.ID}>
                <Card
                  sx={{
                    height: '100%',
                    minHeight: '250px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    bgcolor: 'white',
                    borderRadius: '15px',
                    p: '30px',
                    position: 'relative',
                  }}
                >
                  <Box>
                    <Typography
                      variant="h3"
                      sx={{ fontSize: { xs: '22px', sm: '25px', md: '25px' }, fontWeight: 400, mb: 2 }}
                    >
                      {job.JobTitle}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        mb: 1,
                        alignItems: { xs: 'center', sm: 'center', md: 'center', lg: 'flex-start', xl: 'center' },
                        gap: 1,
                        flexDirection: { xs: 'row', sm: 'row', md: 'row', lg: 'column', xl: 'row' },
                      }}
                    >
                      <Box sx={{ display: 'flex', mb: 1, alignItems: 'center', gap: 0.5 }}>
                        <WorkIcon sx={{ color: '#0087B4', fontSize: 20 }} />
                        <Typography
                          sx={{
                            fontWeight: 300,
                            fontSize: { xs: '12px', sm: '14px', md: '12px', lg: '18px', xl: '18px' },
                          }}
                        >
                          {job.JobType}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', mb: 1, alignItems: 'center', gap: 0.5 }}>
                        <LocationOnIcon sx={{ color: '#0087B4', fontSize: 20 }} />
                        <Typography
                          sx={{
                            fontWeight: 300,
                            fontSize: { xs: '12px', sm: '14px', md: '12px', lg: '18px', xl: '18px' },
                          }}
                        >
                          {job.Location}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        mb: 3,
                        fontWeight: 300,
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        fontSize: '18px',
                      }}
                    >
                      {job.JobAbout}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '15px',
                      left: '30px',
                    }}
                  >
                    <ArrowForwardIcon
                      onClick={() => navigate(`/job/${job.ID}`)}
                      sx={{
                        cursor: 'pointer',
                        color: '#0087B4',
                      }}
                    />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default JobList;